import {createTheme, assignTokens} from '@sail/engine';
import {tokens as $} from '@sail/tokens';

const visualRefreshColorTokens = {
  // Slate
  'color.hue.slate.0': '#FFFFFF',
  'color.hue.slate.25': '#F6F9FC',
  'color.hue.slate.33': '#F1F6FB',
  'color.hue.slate.50': '#E9F0F7',
  'color.hue.slate.100': '#D3DEE9',
  'color.hue.slate.200': '#B1BFCE',
  'color.hue.slate.300': '#B1BFCE',
  'color.hue.slate.400': '#7D8FA2',
  'color.hue.slate.500': '#4F647A',
  'color.hue.slate.600': '#4F647A',
  'color.hue.slate.700': '#4F647A',
  'color.hue.slate.800': '#304257',
  'color.hue.slate.900': '#142333',
  'color.hue.slate.1000': '#142333',

  // Blurple
  'color.hue.blurple.0': '#FFFFFF',
  'color.hue.blurple.25': '#F8F8FF',
  'color.hue.blurple.33': '#F4F4FF',
  'color.hue.blurple.50': '#EDEEFD',
  'color.hue.blurple.100': '#D6DBFA',
  'color.hue.blurple.200': '#B1BAF5',
  'color.hue.blurple.300': '#B1BAF5',
  'color.hue.blurple.400': '#7880F8',
  'color.hue.blurple.500': '#533AFD',
  'color.hue.blurple.600': '#533AFD',
  'color.hue.blurple.700': '#361FB3',
  'color.hue.blurple.800': '#361FB3',
  'color.hue.blurple.900': '#1B1463',
  'color.hue.blurple.1000': '#1B1463',

  // Blue
  'color.hue.blue.0': '#FFFFFF',
  'color.hue.blue.25': '#F2FAFF',
  'color.hue.blue.33': '#EEF7FF',
  'color.hue.blue.50': '#E4F1FF',
  'color.hue.blue.100': '#C5DFFD',
  'color.hue.blue.200': '#97C1F3',
  'color.hue.blue.300': '#97C1F3',
  'color.hue.blue.400': '#438DEE',
  'color.hue.blue.500': '#005CCC',
  'color.hue.blue.600': '#005CCC',
  'color.hue.blue.700': '#013A96',
  'color.hue.blue.800': '#013A96',
  'color.hue.blue.900': '#041F51',
  'color.hue.blue.1000': '#041F51',

  // Red
  'color.hue.red.0': '#FFFFFF',
  'color.hue.red.25': '#FFF6F7',
  'color.hue.red.33': '#FEF2F3',
  'color.hue.red.50': '#FEEBED',
  'color.hue.red.100': '#FBD5D8',
  'color.hue.red.200': '#F7A9AE',
  'color.hue.red.300': '#F7A9AE',
  'color.hue.red.400': '#EF5B71',
  'color.hue.red.500': '#C4053F',
  'color.hue.red.600': '#C4053F',
  'color.hue.red.700': '#830A2E',
  'color.hue.red.800': '#830A2E',
  'color.hue.red.900': '#490518',
  'color.hue.red.1000': '#490518',

  // Orange
  'color.hue.orange.0': '#FFFFFF',
  'color.hue.orange.25': '#FEF8F1',
  'color.hue.orange.33': '#FEF4EC',
  'color.hue.orange.50': '#FDECE0',
  'color.hue.orange.100': '#FFD5B3',
  'color.hue.orange.200': '#F6AD88',
  'color.hue.orange.300': '#F6AD88',
  'color.hue.orange.400': '#F25F23',
  'color.hue.orange.500': '#B0390A',
  'color.hue.orange.600': '#B0390A',
  'color.hue.orange.700': '#762510',
  'color.hue.orange.800': '#762510',
  'color.hue.orange.900': '#450E05',
  'color.hue.orange.1000': '#450E05',

  // Green
  'color.hue.green.0': '#FFFFFF',
  'color.hue.green.25': '#F2FBF9',
  'color.hue.green.33': '#ECF8F6',
  'color.hue.green.50': '#E2F3F0',
  'color.hue.green.100': '#C1E4DE',
  'color.hue.green.200': '#91CAC1',
  'color.hue.green.300': '#91CAC1',
  'color.hue.green.400': '#149E91',
  'color.hue.green.500': '#006F67',
  'color.hue.green.600': '#006F67',
  'color.hue.green.700': '#044943',
  'color.hue.green.800': '#044943',
  'color.hue.green.900': '#002723',
  'color.hue.green.1000': '#002723',
};

const shadowSmall =
  '0 2px 4px -1px rgba(20, 34, 52, 0.05), 0 1px 2px 0 rgba(20, 34, 52, 0.06), 0 0 3px 0 rgba(20, 34, 52, 0.06)';
const shadowMedium = `0 8px 16px -4px rgba(20, 34, 52, 0.05), ${shadowSmall}`;
const shadowLarge = `0 16px 32px -8px rgba(20, 34, 52, 0.04), ${shadowMedium}`;
const shadowXlarge = `0 32px 64px -16px rgba(20, 34, 52, 0.04), ${shadowLarge}`;

const visualRefreshShadowTokens = {
  'shadow.small': shadowSmall,
  'shadow.medium': shadowMedium,
  'shadow.large': shadowLarge,
  'shadow.xlarge': shadowXlarge,
};

/** Dashboard visual refresh: new color hue ramps and elevation shadows. */
export const visualRefreshTokens = createTheme(
  {
    name: 'Visual Refresh Tokens',
    description: 'Color and shadow tokens for the visual refresh.',
    category: 'Visual Refresh',
    internal: true,
  },
  [
    assignTokens($, {
      ...visualRefreshColorTokens,
      ...visualRefreshShadowTokens,
    }),
  ],
);
